// clashDetection.ts — Spatial clash detection for tilt-up panel geometry
//
// Detects three categories:
//   A. Panel-vs-Panel  — two panels share 3D volume (corner overlap)
//   B. Feature-vs-Feature — two openings overlap on the same panel face
//   C. Feature-vs-Edge — an opening extends past the panel boundary
//
// Detection is pure geometry math (no GPU, no physics engine).
// Runs in useMemo on data change, not per animation frame.

import type { PanelData, ClashResult, Slot } from '../types/panelvar';
import type { FootprintResult } from './buildingFootprint';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Axis-aligned bounding box in 2D (panel-local coordinates) */
interface Rect {
  label: string;
  entityId: string;
  cx: number;
  cy: number;
  hw: number;  // half-width
  hh: number;  // half-height
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    Math.abs(a.cx - b.cx) < a.hw + b.hw &&
    Math.abs(a.cy - b.cy) < a.hh + b.hh
  );
}

/** Collect all enabled rectangular openings on a panel as Rects */
function collectOpeningRects(panel: PanelData): Rect[] {
  const rects: Rect[] = [];
  const ffeY = -(panel.mpvar.botElev ?? 0) * 12;

  const addSlots = <T extends { x: number; y: number; width: number; height: number; mark: string }>(
    slots: Slot<T>[],
    prefix: string,
  ) => {
    slots.forEach((s, i) => {
      if (!s.enabled) return;
      const d = s.data;
      if (!d.width || !d.height) return;
      rects.push({
        label: `${prefix}-${i} (${d.mark})`,
        entityId: `${prefix}-${i}`,
        cx: d.x,
        cy: d.y + ffeY,
        hw: d.width / 2,
        hh: d.height / 2,
      });
    });
  };

  addSlots(panel.rovar, 'rovar');
  addSlots(panel.wdvar, 'wdvar');
  addSlots(panel.drvar, 'drvar');
  addSlots(panel.dlvar, 'dlvar');
  addSlots(panel.sbvar, 'sbvar');

  // Round blockouts → bounding square
  panel.rbvar.forEach((s, i) => {
    if (!s.enabled || !s.data.diameter) return;
    const r = s.data.diameter / 2;
    rects.push({
      label: `rbvar-${i} (${s.data.mark})`,
      entityId: `rbvar-${i}`,
      cx: s.data.x,
      cy: s.data.y + ffeY,
      hw: r,
      hh: r,
    });
  });

  return rects;
}

// ── Category B: Feature-vs-Feature ───────────────────────────────────────────

function detectFeatureClashes(panel: PanelData, worldX: number, worldY: number, worldZ: number): ClashResult[] {
  const results: ClashResult[] = [];
  const rects = collectOpeningRects(panel);

  for (let i = 0; i < rects.length; i++) {
    for (let j = i + 1; j < rects.length; j++) {
      if (rectsOverlap(rects[i], rects[j])) {
        results.push({
          type: 'feature-feature',
          severity: 'error',
          panelIds: [panel.id],
          description: `${rects[i].label} overlaps ${rects[j].label} on panel ${panel.mpvar.panelMark}`,
          worldX,
          worldY: worldY + (rects[i].cy + rects[j].cy) / 2,
          worldZ,
        });
      }
    }
  }

  return results;
}

// ── Category C: Feature-vs-Edge ──────────────────────────────────────────────

function detectEdgeClashes(panel: PanelData, worldX: number, worldY: number, worldZ: number): ClashResult[] {
  const results: ClashResult[] = [];
  const rects = collectOpeningRects(panel);
  const pw = panel.mpvar.width;
  const ph = panel.mpvar.height;

  for (const r of rects) {
    const left  = r.cx - r.hw;
    const right = r.cx + r.hw;
    const bot   = r.cy - r.hh;
    const top   = r.cy + r.hh;

    let overhang = 0;
    let side = '';
    if (left < 0)   { overhang = Math.max(overhang, -left); side = 'left'; }
    if (right > pw)  { overhang = Math.max(overhang, right - pw); side = 'right'; }
    if (bot < 0)     { overhang = Math.max(overhang, -bot); side = 'bottom'; }
    if (top > ph)    { overhang = Math.max(overhang, top - ph); side = 'top'; }

    if (overhang > 0.5) {  // ignore sub-half-inch rounding artifacts
      results.push({
        type: 'feature-edge',
        severity: overhang > 6 ? 'error' : 'warning',
        panelIds: [panel.id],
        description: `${r.label} extends ${overhang.toFixed(1)}" past ${side} edge of panel ${panel.mpvar.panelMark}`,
        worldX,
        worldY: worldY + r.cy,
        worldZ,
      });
    }
  }

  return results;
}

// ── Category A: Panel-vs-Panel (corner overlap) ─────────────────────────────

interface PanelWorldBox {
  panelId: string;
  mark: string;
  // World-space center
  cx: number;
  cy: number;
  cz: number;
  // Half-extents along the wall's local axes (rotated to world)
  // We store the four corners of the panel footprint in plan view (XZ)
  // plus height extents in Y
  cosA: number;
  sinA: number;
  hw: number;   // half-width (along wall)
  ht: number;   // half-thickness (perpendicular to wall)
  hh: number;   // half-height (vertical)
  yBase: number; // bottom Y in world
}

/** Compute world-space OBB corners for a panel within a wall segment */
function panelWorldBox(
  panel: PanelData,
  localX: number,  // panel left edge X offset within wall
  seg: { x: number; z: number; rotationY: number },
): PanelWorldBox {
  const mp = panel.mpvar;
  const hw = mp.width / 2;
  const ht = mp.thickness / 2;
  const hh = mp.height / 2;
  const ffeY = (mp.botElev ?? 0) * 12;

  // Panel center in wall-local coords
  const localCx = localX + hw;
  const localCz = 0;

  // Rotate to world — THREE.js Y-rotation: x' = x·cos + z·sin, z' = -x·sin + z·cos
  const cosA = Math.cos(seg.rotationY);
  const sinA = Math.sin(seg.rotationY);
  const worldCx = seg.x + localCx * cosA + localCz * sinA;
  const worldCz = seg.z - localCx * sinA + localCz * cosA;

  return {
    panelId: panel.id,
    mark: mp.panelMark,
    cx: worldCx,
    cy: ffeY + hh,
    cz: worldCz,
    cosA,
    sinA,
    hw,
    ht,
    hh,
    yBase: ffeY,
  };
}

/** Separating-axis test for two 2D OBBs (plan view XZ) */
function obbOverlap2D(a: PanelWorldBox, b: PanelWorldBox): boolean {
  // Each OBB has two axes: along-wall (cos, -sin) and across-wall (sin, cos)
  const axes = [
    { x: a.cosA, z: -a.sinA },
    { x: a.sinA, z: a.cosA },
    { x: b.cosA, z: -b.sinA },
    { x: b.sinA, z: b.cosA },
  ];

  const dx = b.cx - a.cx;
  const dz = b.cz - a.cz;

  for (const ax of axes) {
    // Project separation onto axis
    const sep = Math.abs(dx * ax.x + dz * ax.z);
    // Project half-extents of A onto axis (along-wall dir = (cos,-sin), across = (sin,cos))
    const projA = a.hw * Math.abs(a.cosA * ax.x + (-a.sinA) * ax.z)
                + a.ht * Math.abs(a.sinA * ax.x + a.cosA * ax.z);
    // Project half-extents of B onto axis
    const projB = b.hw * Math.abs(b.cosA * ax.x + (-b.sinA) * ax.z)
                + b.ht * Math.abs(b.sinA * ax.x + b.cosA * ax.z);
    if (sep > projA + projB) return false;  // separated (exact tolerance — joint gaps handle spacing)
  }
  return true;  // no separating axis found → overlap
}

function detectPanelPanelClashes(
  footprint: FootprintResult,
  segments: Array<{ elevId: string; x: number; z: number; rotationY: number; panels: PanelData[]; leftEndJoint: number; rightEndJoint: number }>,
  jointWidth: number,
): ClashResult[] {
  const results: ClashResult[] = [];

  // Build a map from elevId to segment
  const segMap = new Map(segments.map(s => [s.elevId, s]));

  // For each wall, check the last panel against the first panel of the next wall
  for (let wi = 0; wi < footprint.walls.length; wi++) {
    const wallA = footprint.walls[wi];
    const wallB = footprint.walls[(wi + 1) % footprint.walls.length];
    if (!wallB || wallA.elevId === wallB.elevId) continue;

    const segA = segMap.get(wallA.elevId);
    const segB = segMap.get(wallB.elevId);
    if (!segA || !segB) continue;
    if (segA.panels.length === 0 || segB.panels.length === 0) continue;

    // Last panel of wall A
    const panelA = segA.panels[segA.panels.length - 1];
    // First panel of wall B
    const panelB = segB.panels[0];

    // Compute local X offsets
    let xA = segA.leftEndJoint;
    for (let i = 0; i < segA.panels.length - 1; i++) {
      xA += segA.panels[i].mpvar.width + jointWidth;
    }
    const xB = segB.leftEndJoint;

    const boxA = panelWorldBox(panelA, xA, segA);
    const boxB = panelWorldBox(panelB, xB, segB);

    // Vertical overlap check (Y axis)
    const aTop = boxA.yBase + panelA.mpvar.height;
    const bTop = boxB.yBase + panelB.mpvar.height;
    const yOverlap = Math.min(aTop, bTop) - Math.max(boxA.yBase, boxB.yBase);
    if (yOverlap <= 0) continue;

    // Plan-view OBB overlap
    if (!obbOverlap2D(boxA, boxB)) continue;

    // Overlap detected — determine severity
    const maxT = Math.max(panelA.mpvar.thickness, panelB.mpvar.thickness);
    // Compute approximate overlap depth along the thinner wall's perpendicular
    const severity: 'error' | 'warning' = 'warning';  // butt-joint corner overlap ≤ T is expected

    results.push({
      type: 'panel-panel',
      severity,
      panelIds: [panelA.id, panelB.id],
      description: `Corner overlap: ${panelA.mpvar.panelMark} ↔ ${panelB.mpvar.panelMark} (${wallA.name} / ${wallB.name}) — panels share ${maxT.toFixed(1)}" of space at butt joint`,
      worldX: (boxA.cx + boxB.cx) / 2,
      worldY: (boxA.cy + boxB.cy) / 2,
      worldZ: (boxA.cz + boxB.cz) / 2,
    });
  }

  return results;
}

// ── Main entry point ─────────────────────────────────────────────────────────

export function detectClashes(
  footprint: FootprintResult,
  segments: Array<{ elevId: string; x: number; z: number; rotationY: number; panels: PanelData[]; leftEndJoint: number; rightEndJoint: number }>,
  jointWidth: number,
): ClashResult[] {
  const results: ClashResult[] = [];

  // Category A: Panel-vs-panel at corners
  results.push(...detectPanelPanelClashes(footprint, segments, jointWidth));

  // Categories B & C: Feature clashes per panel (within each segment)
  for (const seg of segments) {
    let localX = seg.leftEndJoint;
    for (let i = 0; i < seg.panels.length; i++) {
      const p = seg.panels[i];
      const hw = p.mpvar.width / 2;
      const ffeY = (p.mpvar.botElev ?? 0) * 12;

      // Panel center in world space — THREE.js Y-rotation: z' = -x·sin + z·cos
      const cosA = Math.cos(seg.rotationY);
      const sinA = Math.sin(seg.rotationY);
      const worldCx = seg.x + (localX + hw) * cosA;
      const worldCz = seg.z - (localX + hw) * sinA;

      results.push(...detectFeatureClashes(p, worldCx, ffeY + p.mpvar.height / 2, worldCz));
      results.push(...detectEdgeClashes(p, worldCx, ffeY, worldCz));

      localX += p.mpvar.width + (i < seg.panels.length - 1 ? jointWidth : 0);
    }
  }

  return results;
}

// ── Per-panel clash detection (no 3D coords needed — for 2D views) ──────────

/** Check a single panel for feature-vs-feature and feature-vs-edge clashes */
export function detectPanelClashes(panel: PanelData): ClashResult[] {
  return [
    ...detectFeatureClashes(panel, 0, 0, 0),
    ...detectEdgeClashes(panel, 0, 0, 0),
  ];
}

/** Return entity IDs involved in any clash + which panel edges are violated */
export function getClashingEntities(panel: PanelData): {
  entityIds: Set<string>;
  edges: Set<'left' | 'right' | 'top' | 'bottom'>;
} {
  const entityIds = new Set<string>();
  const edges = new Set<'left' | 'right' | 'top' | 'bottom'>();
  const rects = collectOpeningRects(panel);
  const pw = panel.mpvar.width;
  const ph = panel.mpvar.height;

  // Feature-vs-feature
  for (let i = 0; i < rects.length; i++) {
    for (let j = i + 1; j < rects.length; j++) {
      if (rectsOverlap(rects[i], rects[j])) {
        entityIds.add(rects[i].entityId);
        entityIds.add(rects[j].entityId);
      }
    }
  }

  // Feature-vs-edge
  for (const r of rects) {
    const left  = r.cx - r.hw;
    const right = r.cx + r.hw;
    const bot   = r.cy - r.hh;
    const top   = r.cy + r.hh;

    let hasEdgeClash = false;
    if (left < -0.5)   { edges.add('left');   hasEdgeClash = true; }
    if (right > pw + 0.5) { edges.add('right');  hasEdgeClash = true; }
    if (bot < -0.5)    { edges.add('bottom'); hasEdgeClash = true; }
    if (top > ph + 0.5) { edges.add('top');    hasEdgeClash = true; }
    if (hasEdgeClash) entityIds.add(r.entityId);
  }

  return { entityIds, edges };
}
